// Copyright 2018 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { CreditMutationType } from "datatypes/CreditsMutation";
import {
    StreamBeamformingProjection,
    StreamBeamformingProjectionProperties,
    StreamHologram,
    StreamHologramProperties,
    StreamSpectrum,
    StreamSpectrumProperties,
} from "datatypes/SoramaLocalApi";
import { CreditsError } from "errors/CreditsError";
import { StreamMeasurementServiceError } from "errors/StreamMeasurementServiceError";
import { StringExtensions } from "extensions/StringExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { ICreditsService } from "services/Credits/ICreditsService";

@autoinject
export class SoundStreamPreviewService {
    private readonly webRequestHandler: IWebRequestHandler;
    private readonly creditsService: ICreditsService;
    private readonly localApi: ILocalApi;

    //#region API Calls
    private static apiPostFix = "Sound";
    private static hologramApiCall = "/" + SoundStreamPreviewService.apiPostFix + "/{0}/Preview/Hologram";
    private static spectrumApiCall = "/" + SoundStreamPreviewService.apiPostFix + "/{0}/Preview/Spectrum";
    private static beamformingProjectionApiCall =
        "/" + SoundStreamPreviewService.apiPostFix + "/{0}/Preview/BeamformingProjection";
    //#endregion

    constructor(webRequestHandler: IWebRequestHandler, creditsService: ICreditsService, localApi: ILocalApi) {
        this.webRequestHandler = webRequestHandler;
        this.creditsService = creditsService;
        this.localApi = localApi;
    }

    public previewHologram(measurementId: string, properties: StreamHologramProperties): Promise<StreamHologram> {
        return this.localApi.stream.then((uri) => {
            const apiCall = uri + StringExtensions.format(SoundStreamPreviewService.hologramApiCall, measurementId);

            return this.webRequestHandler
                .createPostRequest<StreamHologram>(apiCall, properties)
                .send()
                .then((x) => x.content)
                .then((x) => {
                    // Crediting
                    const action = this.creditsService
                        .getDebetActionCosts()
                        .find((x) => x.creditDebetType == CreditMutationType.NearField);
                    if (-action.amount > this.creditsService.currentCreditSaldo) {
                        // Stop if out of credits
                        throw new CreditsError("Out of credits");
                    } else {
                        // update in background
                        this.creditsService.creditSaldoMutation(action);
                    }
                    return x;
                })
                .catch((e) => Promise.reject(new StreamMeasurementServiceError(e)));
        });
    }

    public previewBeamformingProjection(
        measurementId: string,
        properties: StreamBeamformingProjectionProperties
    ): Promise<StreamBeamformingProjection> {
        return this.localApi.stream.then((uri) => {
            const apiCall =
                uri + StringExtensions.format(SoundStreamPreviewService.beamformingProjectionApiCall, measurementId);

            return this.webRequestHandler
                .createPostRequest<StreamBeamformingProjection>(apiCall, properties)
                .send()
                .then((x) => x.content)
                .then((x) => {
                    // Crediting
                    const action = this.creditsService
                        .getDebetActionCosts()
                        .find((x) => x.creditDebetType == CreditMutationType.Beamforming);
                    if (-action.amount > this.creditsService.currentCreditSaldo) {
                        // Stop if out of credits
                        throw new CreditsError("Out of credits");
                    } else {
                        // update in background
                        this.creditsService.creditSaldoMutation(action);
                    }
                    return x;
                })
                .catch((e) => Promise.reject(new StreamMeasurementServiceError(e)));
        });
    }

    public previewSpectrum(measurementId: string, properties?: StreamSpectrumProperties): Promise<StreamSpectrum> {
        return this.localApi.stream.then((uri) => {
            const apiCall = uri + StringExtensions.format(SoundStreamPreviewService.spectrumApiCall, measurementId);

            return this.webRequestHandler
                .createPostRequest<StreamSpectrum>(apiCall, properties)
                .send()
                .then((x) => x.content)
                .then((x) => {
                    // Crediting
                    const action = this.creditsService
                        .getDebetActionCosts()
                        .find((x) => x.creditDebetType == CreditMutationType.Spectrum);
                    if (-action.amount > this.creditsService.currentCreditSaldo) {
                        // Stop if out of credits
                        throw new CreditsError("Out of credits");
                    } else {
                        // update in background
                        this.creditsService.creditSaldoMutation(action);
                    }
                    return x;
                })
                .catch((e) => Promise.reject(new StreamMeasurementServiceError(e)));
        });
    }
}
